import { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { roleHasCapability } from '@shared/accessPolicy';
import { isIpClusterId } from '@shared/ipClusters';
import {
  isMathSubjectApplicable,
  MATH_SUBJECTS,
  type MathSubject,
  resolveMathSubjectFocus,
} from '@shared/mathSubjects';
import { fetchFreshIdToken } from '@/auth/freshIdToken';
import { sandboxStorageKey } from '@/auth/sandboxSelection';
import { adaptivePracticeApi } from '@/services/adaptivePracticeApi';

import { useAuth } from './useAuth';

/**
 * The "trying out as" cohort a staff user (super_admin / admin / teacher) has
 * picked. Mirrors the server's `parseRequestedStaffLevelStream` payload —
 * always normalised to non-empty trimmed strings.
 */
export interface StaffLevelStreamPair {
  orgLevel: string;
  examLevel: string;
  /**
   * IP school sequence to preview. Only meaningful on an IP stream, and only
   * for a registered cluster — the server re-checks both, so this is UX.
   */
  cluster?: string | null;
  /**
   * E-Math / A-Math paper to preview. Only meaningful where the split is real
   * (`isMathSubjectApplicable`: Sec 3/4 at G2/G3); absent or null means the
   * default paper, E-Math (`staffSimMathSubject`). The server re-checks it, so
   * this is UX.
   */
  mathSubject?: MathSubject | null;
}

/**
 * The paper a staff preview serves. A simulated cohort sits both papers, so
 * the student rule applies: the picked paper, else E-Math, and none where the
 * cohort has no split. Never both papers at once.
 */
export const staffSimMathSubject = (
  pair: StaffLevelStreamPair | null | undefined
): MathSubject | null =>
  resolveMathSubjectFocus(
    { focusMathSubject: pair?.mathSubject, mathSubjects: MATH_SUBJECTS },
    pair
  ).mathSubject;

export interface UseStaffLevelStreamResult {
  /** True when the actor's role allows staff-sim. False for students. */
  enabled: boolean;
  /** Initial metadata fetch in flight. */
  isLoading: boolean;
  /**
   * Available level/stream pairs. For teachers, this is the dedup'd set of
   * tagged students' levels (server-derived). For admin/super_admin, it's the
   * full Singapore school set. Empty when not yet loaded or no options exist
   * (e.g. teacher with no tagged students).
   */
  options: StaffLevelStreamPair[];
  /** Currently active cohort, or null when nothing is selected. */
  selected: StaffLevelStreamPair | null;
  /** Update the active cohort and persist to sessionStorage. */
  setSelected: (pair: StaffLevelStreamPair | null) => void;
}

const STORAGE_KEY = sandboxStorageKey('adaptive.staffLevelStream');

const trimToString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toMathSubject = (value: unknown): MathSubject | null => {
  const candidate = trimToString(value);
  return MATH_SUBJECTS.find(subject => subject === candidate) ?? null;
};

const normalizePair = (raw: unknown): StaffLevelStreamPair | null => {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const orgLevel = trimToString(obj.orgLevel);
  const examLevel = trimToString(obj.examLevel);
  if (!orgLevel || !examLevel) return null;
  // A cluster only survives on an IP stream and only while its registry entry
  // exists, so a stored pick from a removed cluster reads back as the base
  // sequence instead of a dangling id.
  const cluster =
    examLevel === 'IP' && isIpClusterId(obj.cluster) ? obj.cluster : null;
  // Same rule for the E-Math/A-Math split: it exists only in Sec 3/4 at G2/G3,
  // so a paper stored against any other cohort is dropped rather than kept as
  // a filter that would silently apply where the split isn't real.
  const mathSubject = isMathSubjectApplicable(examLevel, orgLevel)
    ? toMathSubject(obj.mathSubject)
    : null;
  // Written only when a subject survives. `normalizePair` also builds the
  // *options* rows, which the server supplies with no subject at all — stamping
  // `mathSubject: null` onto every cohort row would assert a choice nobody
  // made.
  return {
    orgLevel,
    examLevel,
    cluster,
    ...(mathSubject ? { mathSubject } : {}),
  };
};

const normalizePairs = (raw: unknown): StaffLevelStreamPair[] => {
  if (!Array.isArray(raw)) return [];
  const dedup = new Map<string, StaffLevelStreamPair>();
  for (const entry of raw) {
    const pair = normalizePair(entry);
    if (!pair) continue;
    // Level + stream is the whole key: the options list is the set of cohorts a
    // staff user may preview, and both `cluster` and `mathSubject` are choices
    // made *within* a cohort, not extra cohorts. Keying on either would split
    // one dropdown row into several identical-looking ones.
    dedup.set(`${pair.orgLevel}|${pair.examLevel}`, pair);
  }
  return [...dedup.values()];
};

const sameCohort = (
  a: StaffLevelStreamPair | null,
  b: StaffLevelStreamPair | null
): boolean =>
  Boolean(a && b && a.orgLevel === b.orgLevel && a.examLevel === b.examLevel);

const readStored = (): StaffLevelStreamPair | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return normalizePair(JSON.parse(raw));
  } catch {
    return null;
  }
};

const writeStored = (pair: StaffLevelStreamPair | null): void => {
  if (typeof window === 'undefined') return;
  try {
    if (pair) {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pair));
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // sessionStorage may throw under quota or private-mode constraints; the
    // hook still works in-memory for the lifetime of the page.
  }
};

/**
 * Drives the adaptive practice page's "trying out as" cohort selector.
 * Returns role-gated options + current selection so the rest of the page can
 * pass the active pair through to the adaptive API as
 * `?orgLevel=…&examLevel=…`.
 *
 * Selection persists in `sessionStorage` so a refresh inside `/practice` keeps
 * the simulated cohort. The default-on-load comes from the server's
 * `selectedLevelStream` (first tagged student for teachers; first available for
 * super_admin) and is overridden by any valid stored pick.
 */
export function useStaffLevelStream(): UseStaffLevelStreamResult {
  const { userRole, isRoleLoaded } = useAuth();
  const { user, getIdTokenClaims, getAccessTokenSilently } = useAuth0();

  const enabled =
    isRoleLoaded && roleHasCapability(userRole, 'practice:simulateCohorts');

  const [selected, setSelectedState] = useState<StaffLevelStreamPair | null>(
    () => readStored()
  );
  const [options, setOptions] = useState<StaffLevelStreamPair[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const userId = user?.sub;

  useEffect(() => {
    if (!enabled) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    void (async () => {
      try {
        const token = await fetchFreshIdToken({
          getIdTokenClaims,
          getAccessTokenSilently,
        });
        if (!token) throw new Error('Authentication required');
        const data = await adaptivePracticeApi.getCohortOptions(token);
        if (cancelled) return;
        const fetched = normalizePairs(data.levelStreamOptions);
        setOptions(fetched);

        const stored = readStored();
        const storedIsValid = fetched.some(option =>
          sameCohort(option, stored)
        );
        if (storedIsValid) {
          setSelectedState(stored);
          return;
        }
        if (fetched.length === 0) {
          setSelectedState(null);
          writeStored(null);
          return;
        }
        const serverSelected = normalizePair(data.selectedLevelStream);
        const fallback =
          (serverSelected &&
            fetched.find(option => sameCohort(option, serverSelected))) ||
          fetched[0];
        setSelectedState(fallback);
        writeStored(fallback);
      } catch {
        if (cancelled) return;
        setOptions([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, getAccessTokenSilently, getIdTokenClaims, userId]);

  const setSelected = useCallback((pair: StaffLevelStreamPair | null) => {
    setSelectedState(pair);
    writeStored(pair);
  }, []);

  return { enabled, isLoading, options, selected, setSelected };
}
